import { useQuery } from '@apollo/client';
import { gql } from '__generated__/gql';
import { CvlApplicationStatusEnumType } from '__generated__/graphql';
import { FC } from 'react';
import { useNavigate } from 'react-router-dom';

export const ACCREDITATION_APPLICATION_ROUTE = '/invest/apply-for-accreditation';

const CVL_APPLICATION_STATUS_QUERY = gql(`
  query InvestorAccreditationApplicationStatusForAccreditation($investmentEntityId: ID!) {
    investorAccreditationApplication(investmentEntityId: $investmentEntityId) {
      id
      status
    }
  }
`);

type CardContent = {
  title: string;
  description: string;
  actionLabel: string;
  tone: 'progress' | 'submitted' | 'attention';
};

/**
 * The CVL application has no row until registration (step 1) completes, so
 * `undefined` here means "routed to this flow but not started yet" rather than
 * "no application".
 */
export const getResumeCardContent = (
  applicationStatus?: CvlApplicationStatusEnumType | null
): CardContent => {
  switch (applicationStatus) {
    case CvlApplicationStatusEnumType.ApplicationVerified:
    case CvlApplicationStatusEnumType.ApplicationApproved:
      return {
        title: 'Submitted successfully',
        description:
          'Your application is with our accreditation partner. We will let you know as soon as there is an update.',
        actionLabel: 'View status',
        tone: 'submitted',
      };
    case CvlApplicationStatusEnumType.ApplicationSubmitted:
      return {
        title: 'Awaiting verification',
        description:
          'Your application has been submitted. You can review what you sent at any time.',
        actionLabel: 'View status',
        tone: 'submitted',
      };
    case CvlApplicationStatusEnumType.ApplicationRejected:
      return {
        title: 'Needs your attention',
        description:
          'Our accreditation partner could not process this application. Open it to see what needs correcting.',
        actionLabel: 'Review application',
        tone: 'attention',
      };
    case CvlApplicationStatusEnumType.Registered:
    case CvlApplicationStatusEnumType.ApplicationDraft:
      return {
        title: 'In progress',
        description:
          'You have started this application but have not submitted it yet. Pick up where you left off.',
        actionLabel: 'Resume Application',
        tone: 'progress',
      };
    default:
      return {
        title: 'Not started',
        description:
          'You can complete your accreditation application here on the platform — no third-party portal needed.',
        actionLabel: 'Start Application',
        tone: 'progress',
      };
  }
};

const TONE_STYLES: Record<CardContent['tone'], { badge: string; dot: string }> = {
  progress: { badge: 'bg-blue-100 text-blue-800 ring-blue-200', dot: 'bg-blue-500' },
  submitted: { badge: 'bg-green-100 text-green-800 ring-green-200', dot: 'bg-green-500' },
  attention: { badge: 'bg-red-100 text-red-800 ring-red-200', dot: 'bg-red-500' },
};

type Props = {
  investmentEntityId: string;
  entityName: string;
};

const AccreditationResumeCard: FC<Props> = ({ investmentEntityId, entityName }) => {
  const navigate = useNavigate();

  const { data } = useQuery(CVL_APPLICATION_STATUS_QUERY, {
    variables: { investmentEntityId },
    fetchPolicy: 'network-only',
  });

  const { title, description, actionLabel, tone } = getResumeCardContent(
    data?.investorAccreditationApplication?.status
  );
  const styles = TONE_STYLES[tone];

  return (
    <div className="flex flex-col gap-4 rounded-xl border border-gray-200 bg-white p-5 shadow-sm sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0">
        <div className="mb-1 flex flex-wrap items-center gap-2">
          <p className="truncate font-semibold text-gray-900">{entityName}</p>
          <span
            className={`inline-flex flex-none items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 ${styles.badge}`}
          >
            <span className={`h-1.5 w-1.5 rounded-full ${styles.dot}`} />
            {title}
          </span>
        </div>
        <p className="text-sm text-gray-600">{description}</p>
      </div>
      <button
        type="button"
        className="inline-flex flex-none items-center justify-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
        onClick={() => navigate(ACCREDITATION_APPLICATION_ROUTE)}
      >
        {actionLabel}
      </button>
    </div>
  );
};

export default AccreditationResumeCard;
